import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Context } from '../shared/services/context.service';

export interface ConversationParticipantView {
  id: number;
  firstname: string;
  lastname: string;
  displayName: string;
  status: string;
  role: string;
}

export interface ConversationResponse {
  id: number;
  participants: ConversationParticipantView[];
  createdAt: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
}

export interface ConversationMessageResponse {
  id: number;
  conversationId: number;
  senderId: number;
  receiverId: number;
  senderName: string;
  body: string;
  createdAt: string;
  read: boolean;
  readAt: string;
  senderStatus: string;
  unreadCount: number;
}

export interface ConversationRosterResponse {
  conversations: ConversationResponse[];
  participants: ConversationParticipantView[];
}

@Injectable({
  providedIn: 'root'
})
export class ConversationService {

  private baseUrl = Context.apiUrl + 'api/conversations';

  constructor(private http: HttpClient) {}

  getConversations(): Observable<ConversationResponse[]> {
    return this.http.get<ConversationResponse[]>(this.baseUrl);
  }

  getRoster(): Observable<ConversationRosterResponse> {
    return this.http.get<ConversationRosterResponse>(this.baseUrl + '/roster');
  }

  getAvailableParticipants(): Observable<ConversationParticipantView[]> {
    return this.http.get<ConversationParticipantView[]>(this.baseUrl + '/available-participants');
  }

  openConversation(participantId: number): Observable<ConversationResponse> {
    return this.http.post<ConversationResponse>(this.baseUrl, { participantId: participantId });
  }

  openConversationWithHistory(participantId: number): Observable<{ conversation: ConversationResponse; messages: ConversationMessageResponse[] }> {
    return this.http.post<{ conversation: ConversationResponse; messages: ConversationMessageResponse[] }>(
      this.baseUrl + '/history',
      { participantId: participantId }
    );
  }

  getMessages(conversationId: number, page?: number, size?: number): Observable<{ messages: ConversationMessageResponse[]; hasNext: boolean }> {
    var params = new HttpParams();
    if (page !== undefined && page !== null) {
      params = params.set('page', String(page));
    }
    if (size !== undefined && size !== null) {
      params = params.set('size', String(size));
    }
    return this.http.get<{ messages: ConversationMessageResponse[]; hasNext: boolean }>(
      this.baseUrl + '/' + conversationId + '/messages',
      { params: params }
    );
  }

  getMessagesHistory(conversationId: number): Observable<ConversationMessageResponse[]> {
    if (conversationId === null || conversationId === undefined) {
      // no conversation yet -> no history to load
      return of([]);
    }
    return this.http.get<ConversationMessageResponse[]>(this.baseUrl + '/' + conversationId + '/messages/history');
  }

  sendMessage(conversationId: number, body: string): Observable<ConversationMessageResponse> {
    return this.http.post<ConversationMessageResponse>(this.baseUrl + '/' + conversationId + '/messages', { body: body });
  }

  markAsRead(conversationId: number): Observable<{ updatedCount: number }> {
    return this.http.post<{ updatedCount: number }>(this.baseUrl + '/' + conversationId + '/read', {});
  }
}


